import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Truck, RotateCcw, Shield, Award, CheckCircle, MapPin, Clock, Sparkles, IndianRupee, BadgeCheck } from 'lucide-react';

const Section = ({ id, children, className = '' }) => (
  <motion.section
    id={id}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.6 }}
    className={className}
    data-testid={`policy-section-${id}`}
  >
    {children}
  </motion.section>
);

const Step = ({ icon: Icon, title, children }) => (
  <div className="flex gap-3 md:gap-4">
    <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-[#fff5f0] flex items-center justify-center flex-shrink-0">
      <Icon size={16} className="text-[#C27054]" />
    </div>
    <div>
      <p className="text-sm md:text-base font-semibold text-[#2D3B26] mb-1">{title}</p>
      <p className="text-sm text-[#475740] leading-relaxed">{children}</p>
    </div>
  </div>
);

const Policies = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-24 md:pt-32 pb-16 md:pb-24" data-testid="policies-page">
      {/* Hero */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 text-center mb-12 md:mb-16">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#C27054] mb-3">Promises</p>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif tracking-tight text-[#2D3B26] leading-[1.1] mb-4">
          Policies & <span className="italic text-[#C27054]">Promises</span>
        </h1>
        <p className="text-base md:text-lg text-[#475740] leading-relaxed max-w-2xl mx-auto">
          Everything we promise — shipping, returns, warranty, and certification — written in plain language. No fine print, no surprises.
        </p>

        {/* Quick links */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          {[
            { id: 'free-shipping', label: 'Free Shipping' },
            { id: 'returns', label: '30-Day Returns' },
            { id: 'warranty', label: 'Lifetime Warranty' },
            { id: 'certified', label: 'Certified Jewellery' },
          ].map((q) => (
            <a
              key={q.id}
              href={`#${q.id}`}
              data-testid={`policy-quicklink-${q.id}`}
              className="text-xs uppercase tracking-widest font-semibold px-4 py-2 rounded-full bg-[#E4E8DF] text-[#2D3B26] hover:bg-[#C27054] hover:text-white transition-colors duration-300"
            >
              {q.label}
            </a>
          ))}
        </div>
      </div>

      {/* FREE SHIPPING */}
      <Section id="free-shipping" className="max-w-4xl mx-auto px-4 md:px-6 mb-16 md:mb-20">
        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-[0_4px_20px_rgba(45,59,38,0.06)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#fff5f0] flex items-center justify-center">
              <Truck size={22} className="text-[#C27054]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#C27054]">Always Included</p>
              <h2 className="text-2xl md:text-3xl font-serif text-[#2D3B26]">Free Shipping</h2>
            </div>
          </div>
          <p className="text-base text-[#475740] leading-relaxed mb-6">
            Every NOVA order ships free across India — no minimum purchase, no surprise charges at checkout. Your jewelry is dispatched within 24 hours of confirmation in a signature NOVA box, fully insured, and tracked door-to-door.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
            <Step icon={Clock} title="Dispatch in 24 hours">
              Orders placed before 6 PM are packed and shipped the same day. Weekend orders dispatch on Monday morning.
            </Step>
            <Step icon={MapPin} title="Tracked nationwide">
              Real-time tracking SMS + email at every stage — packed, dispatched, in-transit, out for delivery, delivered.
            </Step>
            <Step icon={Shield} title="Fully insured">
              Every package is insured for its full value. Lost or damaged in transit? We replace at zero cost to you.
            </Step>
            <Step icon={CheckCircle} title="Signature on delivery">
              For orders above ₹10,000 we require a signature so your jewelry never sits unattended on a doorstep.
            </Step>
          </div>
          <div className="bg-[#E4E8DF] rounded-xl p-4 text-sm text-[#475740]">
            <strong className="text-[#2D3B26]">Delivery timeline:</strong> Metro cities — 3 to 5 business days · Tier-2/Tier-3 cities — 5 to 7 business days · Remote pincodes — 7 to 10 business days. International shipping is currently invite-only; contact us for a quote.
          </div>
        </div>
      </Section>

      {/* 30-DAY RETURNS */}
      <Section id="returns" className="max-w-4xl mx-auto px-4 md:px-6 mb-16 md:mb-20">
        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-[0_4px_20px_rgba(45,59,38,0.06)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#fff5f0] flex items-center justify-center">
              <RotateCcw size={22} className="text-[#C27054]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#C27054]">No Questions Asked</p>
              <h2 className="text-2xl md:text-3xl font-serif text-[#2D3B26]">30-Day Returns</h2>
            </div>
          </div>
          <p className="text-base text-[#475740] leading-relaxed mb-6">
            Not in love with your piece? Return it within <strong className="text-[#2D3B26]">30 days of delivery</strong> for a full refund. After our quality team verifies the product&rsquo;s condition and weight, we refund <strong className="text-[#C27054]">100% of the invoice value</strong> back to your original payment method — no restocking fees, no deductions.
          </p>

          <div className="bg-[#fff5f0] border border-[#C27054]/20 rounded-xl p-5 mb-6">
            <p className="text-sm font-semibold text-[#2D3B26] mb-3 uppercase tracking-wider">How It Works</p>
            <div className="space-y-4">
              <Step icon={CheckCircle} title="1. Request your return">
                Email <strong>support@novajewels.in</strong> or visit My Orders → Request Return within 30 days of delivery. Free reverse pickup is arranged at your doorstep.
              </Step>
              <Step icon={Sparkles} title="2. Quality &amp; weight check">
                Once we receive your piece, our certified team verifies the product quality, weight, and BIS hallmark match the original invoice — typically within 48 hours.
              </Step>
              <Step icon={RotateCcw} title="3. 100% invoice refund">
                Refund of the full invoice amount is processed to your original payment method within 5 business days. UPI gets it instantly; cards take 3–5 business days bank-side.
              </Step>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-green-800 uppercase tracking-wider mb-2">✓ Eligible for return</p>
              <ul className="text-sm text-green-900 space-y-1 list-disc list-inside">
                <li>Unworn pieces with original tags</li>
                <li>Original NOVA packaging &amp; certificate</li>
                <li>Items returned within 30 days of delivery</li>
              </ul>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-red-800 uppercase tracking-wider mb-2">✗ Not eligible</p>
              <ul className="text-sm text-red-900 space-y-1 list-disc list-inside">
                <li>Customised &amp; engraved pieces</li>
                <li>Pierced earrings (hygiene reasons)</li>
                <li>Damaged or altered items</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* LIFETIME WARRANTY */}
      <Section id="warranty" className="max-w-4xl mx-auto px-4 md:px-6 mb-16 md:mb-20">
        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-[0_4px_20px_rgba(45,59,38,0.06)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#fff5f0] flex items-center justify-center">
              <Shield size={22} className="text-[#C27054]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#C27054]">For Life</p>
              <h2 className="text-2xl md:text-3xl font-serif text-[#2D3B26]">Lifetime Warranty</h2>
            </div>
          </div>
          <p className="text-base text-[#475740] leading-relaxed mb-6">
            Buy a NOVA piece once, treasure it forever. If your jewelry gets <strong className="text-[#2D3B26]">damaged in any way</strong> — bent prong, broken clasp, dulled finish, loose stone, snapped chain — we&rsquo;ll <strong className="text-[#C27054]">repair or restore it for life</strong>, no matter how many years have passed since purchase.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            <div className="bg-[#E4E8DF] rounded-xl p-5">
              <p className="text-sm font-semibold text-[#2D3B26] mb-3 uppercase tracking-wider">What we cover</p>
              <ul className="text-sm text-[#475740] space-y-2">
                <li className="flex gap-2"><span className="text-[#C27054]">•</span> Bent or broken prongs</li>
                <li className="flex gap-2"><span className="text-[#C27054]">•</span> Snapped chains or broken clasps</li>
                <li className="flex gap-2"><span className="text-[#C27054]">•</span> Loose or fallen stones (re-setting)</li>
                <li className="flex gap-2"><span className="text-[#C27054]">•</span> Dulled polish (free buffing &amp; rhodium replating)</li>
                <li className="flex gap-2"><span className="text-[#C27054]">•</span> Resizing rings (one free resize per year)</li>
                <li className="flex gap-2"><span className="text-[#C27054]">•</span> Restringing pearls or beads</li>
              </ul>
            </div>
            <div className="bg-[#fff5f0] border border-[#C27054]/20 rounded-xl p-5">
              <p className="text-sm font-semibold text-[#2D3B26] mb-3 uppercase tracking-wider">How to claim</p>
              <ol className="text-sm text-[#475740] space-y-2 list-decimal list-inside">
                <li>Email <strong>care@novajewels.in</strong> with photos of the damage and your order ID.</li>
                <li>We arrange free reverse pickup nationwide.</li>
                <li>Our master jewelers diagnose and repair within 7–14 days.</li>
                <li>Piece is sent back to you, fully restored.</li>
                <li>If repair is not possible, we replace with an equivalent piece.</li>
              </ol>
            </div>
          </div>

          <div className="text-xs text-[#7A8A73] italic leading-relaxed">
            Note: Lifetime warranty is non-transferable and tied to the original buyer&rsquo;s account. Loss, theft, or intentional damage are not covered. For accidental loss, please consider adding jewelry insurance to your home policy.
          </div>
        </div>
      </Section>

      {/* CERTIFIED JEWELLERY */}
      <Section id="certified" className="max-w-4xl mx-auto px-4 md:px-6 mb-16 md:mb-20">
        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-[0_4px_20px_rgba(45,59,38,0.06)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#fff5f0] flex items-center justify-center">
              <Award size={22} className="text-[#C27054]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#C27054]">Government-Verified</p>
              <h2 className="text-2xl md:text-3xl font-serif text-[#2D3B26]">Certified Jewellery</h2>
            </div>
          </div>
          <p className="text-base text-[#475740] leading-relaxed mb-6">
            Every NOVA gold piece is <strong className="text-[#2D3B26]">BIS Hallmark Certified</strong> by the Bureau of Indian Standards — the gold standard (literally) for purity verification in India. The hallmark is your government-issued proof that what you paid for is exactly what you received.
          </p>

          <div className="bg-[#E4E8DF] rounded-xl p-5 md:p-6 mb-6">
            <p className="text-sm font-semibold text-[#2D3B26] mb-4 uppercase tracking-wider">What is BIS Hallmark?</p>
            <p className="text-sm text-[#475740] leading-relaxed mb-4">
              BIS (Bureau of Indian Standards) is the national standards body of India under the Ministry of Consumer Affairs. Since June 2021, BIS Hallmarking has been <strong>mandatory</strong> for all gold jewelry sold in India. The hallmark guarantees the gold purity is exactly what the seller claims — verified by an independent government-approved Assaying &amp; Hallmarking Centre.
            </p>
            <p className="text-sm font-semibold text-[#2D3B26] mb-2">Every NOVA hallmark contains 4 marks:</p>
            <ul className="text-sm text-[#475740] space-y-2">
              <li className="flex gap-2"><span className="text-[#C27054] font-bold">1.</span> <span><strong>BIS standard mark</strong> — the official triangular logo confirming compliance.</span></li>
              <li className="flex gap-2"><span className="text-[#C27054] font-bold">2.</span> <span><strong>Purity grade</strong> — 916 (22K), 750 (18K), 585 (14K), 375 (9K).</span></li>
              <li className="flex gap-2"><span className="text-[#C27054] font-bold">3.</span> <span><strong>Assaying centre&rsquo;s identification mark</strong> — the lab that tested the piece.</span></li>
              <li className="flex gap-2"><span className="text-[#C27054] font-bold">4.</span> <span><strong>HUID (Hallmark Unique ID)</strong> — a 6-digit alphanumeric code unique to your piece.</span></li>
            </ul>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
            {[
              { p: '916', k: '22K', desc: '91.6% pure' },
              { p: '750', k: '18K', desc: '75.0% pure' },
              { p: '585', k: '14K', desc: '58.3% pure' },
              { p: '375', k: '9K', desc: '37.5% pure' },
            ].map((p) => (
              <div key={p.p} className="bg-[#fff5f0] border border-[#C27054]/20 rounded-xl p-3 md:p-4 text-center">
                <p className="text-2xl md:text-3xl font-serif text-[#C27054] font-bold">{p.p}</p>
                <p className="text-xs uppercase tracking-widest font-semibold text-[#2D3B26] mt-1">{p.k}</p>
                <p className="text-[10px] text-[#7A8A73] mt-1">{p.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-green-900 mb-2 flex items-center gap-2">
              <CheckCircle size={16} /> Verify Your NOVA Piece
            </p>
            <p className="text-sm text-green-800 leading-relaxed">
              Use the HUID printed on your jewelry certificate to verify authenticity directly with BIS at{' '}
              <a href="https://www.bis.gov.in" target="_blank" rel="noreferrer" className="underline font-semibold hover:text-green-700">www.bis.gov.in</a>. The BIS portal will confirm the piece, its purity grade, and the assaying centre that certified it.
            </p>
          </div>
        </div>
      </Section>

      {/* LIFETIME BUYBACK */}
      <Section id="buyback" className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-[0_4px_20px_rgba(45,59,38,0.06)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#fff5f0] flex items-center justify-center">
              <IndianRupee size={22} className="text-[#C27054]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#C27054]">The NOVA Promise</p>
              <h2 className="text-2xl md:text-3xl font-serif text-[#2D3B26]">Lifetime Buyback</h2>
            </div>
          </div>
          <p className="text-base text-[#475740] leading-relaxed mb-6">
            Every NOVA gold piece comes with a <strong className="text-[#2D3B26]">100% lifetime buyback guarantee</strong>. Whenever you choose to part with your jewelry — be it next year or thirty years from now — we&rsquo;ll repurchase it at the live market gold rate, with full transparency.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-6">
            <Step icon={IndianRupee} title="100% Gold Value">Buyback at live market 24K rate × certified purity × weight. No depreciation, no discount.</Step>
            <Step icon={BadgeCheck} title="No Expiry">Valid for the lifetime of the original buyer. No time limits, no enrolment fees.</Step>
            <Step icon={Shield} title="BIS Hallmark Verified">Pre-verified hallmark accelerates the valuation — buyback completed within 24 hours.</Step>
            <Step icon={Award} title="Transparent Valuation">Written valuation report shared with you, signed by our certified appraiser.</Step>
          </div>

          <div className="bg-[#E4E8DF] rounded-xl p-5 md:p-6 mb-4">
            <p className="text-sm font-semibold text-[#2D3B26] mb-3 uppercase tracking-wider">How it works</p>
            <ol className="text-sm text-[#475740] space-y-2 list-decimal list-inside">
              <li>Bring your NOVA piece + original invoice / certificate to any NOVA store, or request a free pickup online.</li>
              <li>Our certified appraiser verifies the BIS hallmark and weighs the piece (in front of you).</li>
              <li>Receive instant valuation at the day&rsquo;s 24K rate × purity factor × net gold weight.</li>
              <li>Choose payment via UPI / bank transfer / store credit — credited within 24 hours.</li>
            </ol>
          </div>

          <div className="text-xs text-[#7A8A73] italic leading-relaxed">
            Note: Making charges, GST, and stones (diamonds / gemstones) are excluded from the gold buyback rate. Stones can be valued separately via our partner gemologist. Pieces showing tampering, melting, or non-NOVA modifications may be ineligible.
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Policies;
